import { Check } from 'lucide-react';
import { useMutation } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { pickName } from '../categories.ts';
import type { CatalogOption } from '../options.ts';
import { InlineCreate } from './InlineCreate.tsx';
import styles from './ProductEditor.module.css';
import type { KnownCodes } from './useOptionAxes.ts';
import { usePageContext } from '../../usePageContext.ts';

export type OptionPickerProps = {
  api: TypedClient;
  option: CatalogOption;
  chosen: readonly string[];
  locked: boolean;
  nameLocale: string;
  onToggle: (valueId: string, freshCodes?: KnownCodes) => void;
};

export const OptionPicker = ({ api, option, chosen, locked, nameLocale, onToggle }: OptionPickerProps) => {
  const { t, locale, describeError, invalidate } = usePageContext();
  const [newValue, setNewValue] = useState('');
  const name = pickName(option.names, locale, option.code);

  const addValue = useMutation({
    mutationFn: (valueName: string) =>
      api.post('/admin/options/{id}/values', {
        path: { id: option.id },
        body: { names: [{ locale: nameLocale, name: valueName }] },
      }),
    onSuccess: async (created) => {
      setNewValue('');
      await invalidate('options');
      onToggle(created.id, new Map([[created.id, created.code]]));
    },
  });

  return (
    <fieldset className={styles['picker']} data-chosen={chosen.length > 0}>
      <legend className={styles['pickerHead']}>
        <span className={styles['pickerName']}>{name}</span>
        <span className={styles['pickerCount']}>
          {locked
            ? t('productEditor.optionLimit')
            : t('productEditor.valuesChosen', { count: chosen.length })}
        </span>
      </legend>
      <div className={styles['chips']} role="group" aria-label={name}>
        {option.values.map((value) => {
          const pressed = chosen.includes(value.id);
          return (
            <button
              key={value.id}
              type="button"
              className={styles['chip']}
              aria-pressed={pressed}
              disabled={locked && !pressed}
              onClick={() => {
                onToggle(value.id);
              }}
            >
              {pressed ? <Check size={16} aria-hidden="true" /> : null}
              {pickName(value.names, locale, value.code)}
            </button>
          );
        })}
      </div>
      {locked ? null : (
        <InlineCreate
          label={t('productEditor.newValueFor', { option: name })}
          actionLabel={t('productEditor.addValue')}
          value={newValue}
          pending={addValue.isPending}
          onChange={setNewValue}
          onCreate={(valueName) => {
            addValue.mutate(valueName);
          }}
        />
      )}
      {addValue.error === null ? null : (
        <p role="alert" className={styles['fieldError']}>
          {describeError(addValue.error)}
        </p>
      )}
    </fieldset>
  );
};
